import { IconUploadOutlined } from '@repo/monochrome-icons';
import { type ReactElement, type ReactNode, useId, useState } from 'react';
import type { DropEvent, DropItem } from 'react-aria';
import type { DropZoneProps as AriaDropZoneProps } from 'react-aria-components';
// oxlint-disable-next-line no-duplicate-imports
import { DropZone as AriaDropZone, Button, FileTrigger } from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Label } from '../form-elements';
import { useFormatBytes } from '../../hooks/use-format-bytes';
import { formatExtentions } from '../../utils/format-extentions';
import { Attachment } from '../attachment';
import styles from './dropzone.module.css';

export interface DropZoneProps extends Omit<AriaDropZoneProps, 'children'> {
  className?: string;
  triggerClassName?: string;
  label?: ReactNode;
  placeholder: string;
  allowsMultiple?: boolean;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  onMaxFilesExceeded?: () => void;
  onSelect?: (e: FileList | null) => void;
  onFilesSelect: (files: File[]) => void;
  onFileRemove?: (file: File, files?: File[]) => void;
  onFilePreview?: (file: File) => void;
  /**
   * When `false`, the built-in file list is hidden and internal file
   * state is not accumulated between selections. Use this when the
   * consumer manages its own document list.
   */
  showFileList?: boolean;
  customErrorMessage?: string;
  clearFilesOnDrop?: boolean;
  customTrigger?: ReactNode;
  files?: File[];
  subtext?: ReactNode;
}

export function DropZone({
  customTrigger,
  className,
  triggerClassName,
  label,
  placeholder,
  allowsMultiple = false,
  acceptedFileTypes = ['*'],
  maxFileSize = 30 * 1e6,
  maxFiles,
  onMaxFilesExceeded,
  onSelect,
  onFilesSelect,
  onFileRemove,
  onFilePreview,
  showFileList = true,
  customErrorMessage,
  clearFilesOnDrop = false,
  files: controlledFiles,
  subtext,
  onDrop,
  ...props
}: DropZoneProps): ReactElement {
  const { formatMessage } = useIntl();
  const labelId = useId();
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { formatBytes } = useFormatBytes();
  const maxFileSizeFormatted = formatBytes(maxFileSize);

  const isControlled = controlledFiles !== undefined;
  const files = isControlled ? controlledFiles : internalFiles;

  const addFiles = (fileItems: File[]): void => {
    const shouldAccumulate = allowsMultiple && showFileList && !clearFilesOnDrop;
    const processedFiles = allowsMultiple
      ? [...(shouldAccumulate ? files : []), ...fileItems]
      : fileItems.slice(0, 1);

    if (maxFiles !== undefined && processedFiles.length > maxFiles) {
      onMaxFilesExceeded?.();
      return;
    }

    if (isControlled) {
      onFilesSelect(processedFiles);
    } else {
      setInternalFiles(processedFiles);
      onFilesSelect(processedFiles);
    }
  };

  const resetError = (): void => {
    setError(null);
  };

  const validateFiles = (filesToValidate: File[]): File[] => {
    return filesToValidate.filter(file => {
      if (!acceptedFileTypes.includes('*') && !acceptedFileTypes.includes(file.type)) {
        const errorMsg = formatMessage(
          { id: 'wrong_file_extension' },
          {
            extension: formatExtentions([file.type]),
            extensions: formatExtentions(acceptedFileTypes),
          }
        );
        setError(errorMsg);
        return false;
      }

      if (file.size > maxFileSize) {
        const errorMsg = formatMessage({ id: 'file_too_big' }, { maxSize: maxFileSizeFormatted });
        setError(errorMsg);
        return false;
      }

      return true;
    });
  };

  const handleSelect = (e: FileList | null): void => {
    resetError();
    const filesFromSelect = Array.from(e || []);
    const validatedFiles = validateFiles(filesFromSelect);
    addFiles(validatedFiles);
    onSelect?.(e);
  };

  const handleDrop = async (e: DropEvent): Promise<void> => {
    resetError();
    const filesFromItems = await transformDropItems(e.items);
    addFiles(filesFromItems);
    onDrop?.(e);
  };

  const transformDropItems = async (items: DropItem[]): Promise<File[]> => {
    // oxlint-disable-next-line require-await
    const filePromises = items.map(async item => {
      if ('getFile' in item) {
        return item.getFile();
      }
      return null;
    });

    const filesFromItems = await Promise.all(filePromises);
    const validFiles = filesFromItems.filter((file): file is File => file !== null);
    return validateFiles(validFiles);
  };

  const removeFile = (file: File): void => {
    const filteredFiles = files.filter(f => f.name !== file.name);
    if (onFileRemove) {
      onFileRemove(file, filteredFiles);
    }
    if (!isControlled) {
      setInternalFiles(filteredFiles);
    }
  };

  const hasErrors = Boolean(customErrorMessage || error);

  return (
    <>
      {Boolean(label) && <Label id={labelId}>{label}</Label>}

      <AriaDropZone
        {...props}
        aria-labelledby={label ? labelId : undefined}
        className={`${className} ${styles.dropzone} ${hasErrors ? styles.error : ''}`}
        data-test-dropzone
        onDrop={handleDrop}
      >
        <FileTrigger
          acceptedFileTypes={acceptedFileTypes}
          allowsMultiple={allowsMultiple}
          data-test-file-trigger
          onSelect={handleSelect}
        >
          <Button
            className={`${styles.fileUploader} ${triggerClassName}`}
            data-test-dropzone-button
          >
            {customTrigger || (
              <>
                <IconUploadOutlined className={styles.icon} />
                <span className={`${styles.placeholder} body-standard`} data-testid="label">
                  {placeholder}
                </span>
              </>
            )}
          </Button>
        </FileTrigger>
      </AriaDropZone>
      {Boolean(hasErrors) && (
        <p className={`caption ${styles.errorMessage}`} data-testid="error-message">
          {customErrorMessage || error}
        </p>
      )}
      {Boolean(!hasErrors && subtext) && (
        <p className={`caption ${styles.subtext}`} data-testid="subtext">
          {subtext}
        </p>
      )}
      {Boolean(files.length > 0 && showFileList) && (
        <div className={`${styles.fileList}`}>
          {files.map(file => (
            <Attachment
              key={file.name}
              onPreview={() => onFilePreview?.(file)}
              onRemove={() => {
                removeFile(file);
              }}
              status="uploaded"
              subtitle={formatBytes(file.size)}
              title={file.name}
            />
          ))}
        </div>
      )}
    </>
  );
}
